import type { FolderCounts } from '../api/types';

export type ProgressKey = 'notSent' | 'inQueue' | 'messaged' | 'logged' | 'irrelevant' | 'other';

export interface ProgressSegment {
  key: ProgressKey;
  label: string;
  count: number;
  /** Share of the bar, 0–100. Segments sum to 100 when there are any leads. */
  pct: number;
  /** Tailwind background class for the bar and the legend swatch. */
  swatch: string;
}

const SEGMENTS: { key: Exclude<ProgressKey, 'other'>; label: string; swatch: string }[] = [
  { key: 'notSent', label: 'Not sent', swatch: 'bg-muted-foreground/30' },
  { key: 'inQueue', label: 'In queue', swatch: 'bg-warning' },
  { key: 'messaged', label: 'Messaged', swatch: 'bg-info' },
  { key: 'logged', label: 'Logged · joined', swatch: 'bg-success' },
  { key: 'irrelevant', label: 'Irrelevant', swatch: 'bg-destructive/70' },
];

export interface FolderProgress {
  total: number;
  /** Leads counted as done for completion, as a whole percentage. */
  donePct: number;
  segments: ProgressSegment[];
}

/**
 * The folder header's progress bar. Buckets come from the server's `counts`;
 * anything the five named buckets don't cover (dismissed, suppressed, marked
 * complete…) is one muted "Other" segment so the bar still adds up.
 */
export function folderProgress(counts: FolderCounts | null | undefined): FolderProgress {
  if (!counts || counts.total <= 0) return { total: 0, donePct: 0, segments: [] };
  const named = SEGMENTS.map((s) => ({ ...s, count: Math.max(0, counts[s.key] ?? 0) }));
  const namedSum = named.reduce((n, s) => n + s.count, 0);
  const other = Math.max(0, counts.total - namedSum);
  const all = other > 0 ? [...named, { key: 'other' as const, label: 'Other', swatch: 'bg-muted-foreground/15', count: other }] : named;
  // Buckets can overlap on an inconsistent payload; scale to whichever is larger.
  const denom = Math.max(counts.total, namedSum);
  const segments = all
    .filter((s) => s.count > 0)
    .map((s) => ({ key: s.key, label: s.label, count: s.count, swatch: s.swatch, pct: (s.count / denom) * 100 }));
  const donePct = Math.round((Math.min(counts.done, counts.total) / counts.total) * 100);
  return { total: counts.total, donePct, segments };
}

/** Legend entries in a stable order, including zero buckets (so the legend doesn't jump). */
export function progressLegend(counts: FolderCounts | null | undefined): { label: string; count: number; swatch: string }[] {
  return SEGMENTS.map((s) => ({ label: s.label, count: counts ? Math.max(0, counts[s.key] ?? 0) : 0, swatch: s.swatch }));
}
